import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type { SectionOverride, ShadowBillSection } from '../types/billTypes';
import { normKey, resolveRetailer, addRetailerNameAlias } from '../utils/retailerResolve';
import type { RetailerRow, ResolveResult } from '../utils/retailerResolve';
import { chargeMapService } from './chargeMapService';

// Moved to utils/retailerResolve.ts (kept import-free of Supabase so a ts-node
// seed script can use resolveRetailer without dragging in the Vite client) and
// re-exported here so every existing importer keeps working unchanged.
export { resolveRetailer, addRetailerNameAlias } from '../utils/retailerResolve';
export type { RetailerRow, ResolveResult } from '../utils/retailerResolve';

// No dedicated table — aliases live in the existing retailer.extn jsonb column
// (extn.charge_aliases: ChargeAlias[]), scoped naturally by whichever retailer
// row they're attached to. Avoids a new table for what's a small per-retailer list.
export type ChargeAlias = {
  raw_description: string;
  canonical_description: string;
  section: ShadowBillSection;
};

type ApiResponse<T> = { success: boolean; data?: T; error?: string };

const norm = (v: string) => v.trim().toLowerCase();

async function getOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('base_organisation_members')
    .select('organisation_id')
    .eq('user_id', user.id)
    .single();
  return data?.organisation_id ?? null;
}

async function fetchRetailers(orgId: string): Promise<RetailerRow[]> {
  const { data, error } = await supabase
    .from('retailer').select('id, name, extn').eq('org_id', orgId);
  if (error) throw error;
  return (data as RetailerRow[] | null) ?? [];
}

const candidateNames = (candidates: RetailerRow[] = []) =>
  candidates.map((c) => c.name).join(', ');

// Resolve a retailer for a write op, mapping non-'single'/'exact' outcomes to errors.
async function resolveForWrite(orgId: string, retailerName: string): Promise<
  { retailer: RetailerRow } | { error: string }
> {
  const retailers = await fetchRetailers(orgId);
  const res = resolveRetailer(retailerName, retailers);
  if (res.status === 'exact' || res.status === 'single') return { retailer: res.retailer! };
  if (res.status === 'ambiguous') {
    return { error: `Retailer "${retailerName}" is ambiguous — choose one of: ${candidateNames(res.candidates)}` };
  }
  return { error: `No retailer record found for "${retailerName}"` };
}

export const chargeMappingService = {
  // Resolve a free-text retailer name to a status + candidates, carrying the full
  // retailer list so the UI can offer "all retailers" when the status is 'none'.
  resolve: async (
    retailerName: string | null,
  ): Promise<ApiResponse<ResolveResult & { retailers: RetailerRow[] }>> => {
    try {
      if (!retailerName) return { success: true, data: { status: 'none', retailers: [] } };
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };
      const [retailers, aliases] = await Promise.all([fetchRetailers(orgId), chargeMapService.retailerAliases()]);
      return { success: true, data: { ...resolveRetailer(retailerName, retailers, aliases), retailers } };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  // Record that `aliasName` (the spelling on this site) means retailer `retailer_id`.
  // Stored on the retailer's extn.retailer_name_aliases so resolveRetailer finds it
  // automatically next time, without touching other extn keys (e.g. charge_aliases).
  chooseRetailer: async (input: { retailer_id: string; alias_name: string }): Promise<ApiResponse<null>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };
      if (!normKey(input.alias_name)) return { success: false, error: 'Empty retailer name' };
      const retailers = await fetchRetailers(orgId);
      const retailer = retailers.find((r) => r.id === input.retailer_id);
      if (!retailer) return { success: false, error: 'Retailer not found' };

      const { error: updErr } = await supabase
        .from('retailer')
        .update({ extn: addRetailerNameAlias(retailer.extn ?? null, input.alias_name) })
        .eq('id', retailer.id);
      if (updErr) throw updErr;
      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  listForRetailer: async (retailerName: string | null): Promise<ApiResponse<ChargeAlias[]>> => {
    try {
      if (!retailerName) return { success: true, data: [] };
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      const retailers = await fetchRetailers(orgId);
      const res = resolveRetailer(retailerName, retailers);
      // Only a confident resolution yields aliases; ambiguous/none => empty list.
      const retailer = res.status === 'exact' || res.status === 'single' ? res.retailer : undefined;
      const aliases = (retailer?.extn as { charge_aliases?: ChargeAlias[] } | null)?.charge_aliases ?? [];
      return { success: true, data: aliases };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  create: async (input: {
    retailer_name: string;
    raw_description: string;
    canonical_description: string;
    section: ShadowBillSection;
  }): Promise<ApiResponse<null>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };
      const resolved = await resolveForWrite(orgId, input.retailer_name);
      if ('error' in resolved) return { success: false, error: resolved.error };
      const { retailer } = resolved;

      const existing = ((retailer.extn as { charge_aliases?: ChargeAlias[] } | null)?.charge_aliases ?? [])
        .filter((a) => norm(a.raw_description) !== norm(input.raw_description));
      const charge_aliases: ChargeAlias[] = [
        ...existing,
        { raw_description: norm(input.raw_description), canonical_description: input.canonical_description.trim(), section: input.section },
      ];

      const { error: updErr } = await supabase
        .from('retailer')
        .update({ extn: { ...(retailer.extn as Record<string, unknown> ?? {}), charge_aliases } })
        .eq('id', retailer.id);
      if (updErr) throw updErr;
      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  // ── Job 59: which section a charge belongs to, per retailer ───────────────
  // Same storage approach as charge_aliases: a small per-retailer list on extn,
  // rather than a table for what is a handful of rows per retailer.
  listSectionOverrides: async (retailerName: string | null): Promise<ApiResponse<SectionOverride[]>> => {
    try {
      if (!retailerName) return { success: true, data: [] };
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      const retailers = await fetchRetailers(orgId);
      const res = resolveRetailer(retailerName, retailers);
      // Only a confident resolution yields overrides; ambiguous/none => empty list.
      const retailer = res.status === 'exact' || res.status === 'single' ? res.retailer : undefined;
      const overrides = (retailer?.extn as { section_overrides?: SectionOverride[] } | null)?.section_overrides ?? [];
      return { success: true, data: overrides };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  setSectionOverride: async (input: {
    retailer_name: string;
    charge: string;
    section: ShadowBillSection;
  }): Promise<ApiResponse<null>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };
      const resolved = await resolveForWrite(orgId, input.retailer_name);
      if ('error' in resolved) return { success: false, error: resolved.error };
      const { retailer } = resolved;

      const existing = ((retailer.extn as { section_overrides?: SectionOverride[] } | null)?.section_overrides ?? [])
        .filter((o) => norm(o.charge) !== norm(input.charge));
      const section_overrides: SectionOverride[] = [
        ...existing,
        { charge: input.charge.trim(), section: input.section },
      ];

      const { error: updErr } = await supabase
        .from('retailer')
        .update({ extn: { ...(retailer.extn as Record<string, unknown> ?? {}), section_overrides } })
        .eq('id', retailer.id);
      if (updErr) throw updErr;
      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  clearSectionOverride: async (input: {
    retailer_name: string;
    charge: string;
  }): Promise<ApiResponse<null>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };
      const resolved = await resolveForWrite(orgId, input.retailer_name);
      if ('error' in resolved) return { success: false, error: resolved.error };
      const { retailer } = resolved;

      const existing = (retailer.extn as { section_overrides?: SectionOverride[] } | null)?.section_overrides ?? [];
      const section_overrides = existing.filter((o) => norm(o.charge) !== norm(input.charge));

      // Nothing stored for this charge — succeed without writing.
      if (section_overrides.length === existing.length) return { success: true, data: null };

      const { error: updErr } = await supabase
        .from('retailer')
        .update({ extn: { ...(retailer.extn as Record<string, unknown> ?? {}), section_overrides } })
        .eq('id', retailer.id);
      if (updErr) throw updErr;
      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  remove: async (input: {
    retailer_name: string;
    raw_description: string;
  }): Promise<ApiResponse<null>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };
      const resolved = await resolveForWrite(orgId, input.retailer_name);
      if ('error' in resolved) return { success: false, error: resolved.error };
      const { retailer } = resolved;

      const existing = (retailer.extn as { charge_aliases?: ChargeAlias[] } | null)?.charge_aliases ?? [];
      const target = norm(input.raw_description);
      const charge_aliases = existing.filter((a) => norm(a.raw_description) !== target);

      // Alias not present — nothing to remove, succeed without writing.
      if (charge_aliases.length === existing.length) return { success: true, data: null };

      const { error: updErr } = await supabase
        .from('retailer')
        .update({ extn: { ...(retailer.extn as Record<string, unknown> ?? {}), charge_aliases } })
        .eq('id', retailer.id);
      if (updErr) throw updErr;
      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },
};
